/**
 * PurchaseOrderRenderer — Sprint 12 hand-designed renderer for displayType='purchase_order'.
 *
 * Per spec §8.5: "Line-items table with column-aware editing; total recompute
 * on edit; line add/remove".
 *
 * Schema contract (v1.1 §B Sprint 2 amendment Q9 GATED `line_items` as a field
 * type — it is INTENTIONALLY not in FieldTypeSchema). We read the line-items
 * grid directly from `evidence.line_items` (or `content.line_items` for
 * authors who namespace under `content.*` to match the email_draft canonical
 * shape) and re-emit edits via `onFieldChange` against the same path. This
 * keeps Pod I's typed-field gate intact while still letting Sprint 12 polish
 * the surface workflow authors actually care about.
 *
 * Schema-declared editableFields (PO header data) still render via the shared
 * FieldRow primitive so AI-edit + manual-edit + validation behave the same as
 * everywhere else. Authors are encouraged to declare:
 *   - content.po.number       (text)
 *   - content.po.vendor       (text)
 *   - content.po.requested_by (text)
 *   - content.po.notes        (multiline, often aiEditable)
 * and any other header fields. We don't hard-code these — whatever is in
 * `schema.editableFields` shows up in the header block.
 */

import * as React from 'react';
import { ShoppingCart, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { readPath } from '@argusai/shared';
import { FieldRow } from '../AutoRenderedApprovalCard';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import type { RendererComponent } from '../AutoRenderedApprovalCard';

// Candidate paths the renderer probes when looking for the line-items array.
// Workflow authors using the email_draft-style `content.*` namespacing get
// the `content.line_items` path; authors who keep evidence flat get the
// top-level `line_items`. We pick whichever resolves to an array first.
const LINE_ITEMS_PATHS = ['content.line_items', 'line_items'] as const;

interface LineItem {
  sku: string;
  description: string;
  qty: number;
  unit_price: number;
  // line_total is derived; we recompute on every render so authors don't
  // need to ship a backend that keeps it in sync.
  [key: string]: unknown;
}

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function asFiniteNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function toLineItems(raw: unknown): LineItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map((r) => ({
      sku: asString(r.sku),
      description: asString(r.description),
      qty: asFiniteNumber(r.qty ?? r.quantity),
      unit_price: asFiniteNumber(r.unit_price ?? r.unitPrice ?? r.price),
      ...r,
    }));
}

function resolveLineItemsPath(evidence: Record<string, unknown>): string {
  for (const p of LINE_ITEMS_PATHS) {
    const v = readPath(evidence, p);
    if (Array.isArray(v)) return p;
  }
  // Default to the namespaced path so newly-created rows land in a
  // predictable place even when the workflow author hasn't seeded one.
  return LINE_ITEMS_PATHS[0];
}

function fmtCurrency(n: number, currencyCode = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currencyCode} ${n.toFixed(2)}`;
  }
}

export const PurchaseOrderRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  // Resolve where line items live in evidence on first read.
  const lineItemsPath = React.useMemo(
    () => resolveLineItemsPath(evidence),
    // Path discovery is stable per approval — only re-resolve when the
    // approval row identity changes (e.g., a refetch swaps in fresh data).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approval.id],
  );

  const rawItems = readPath(evidence, lineItemsPath);
  const items = React.useMemo(() => toLineItems(rawItems), [rawItems]);

  // Currency hint — authors can set evidence.currency = 'EUR' etc.
  const currencyCode =
    asString(readPath(evidence, 'content.currency') || readPath(evidence, 'currency')) ||
    'USD';

  // PO summary fields the header block surfaces inline. These are
  // best-effort lookups; if the schema declares them as editableFields they
  // also render below via FieldRow.
  const poNumber = asString(
    readPath(evidence, 'content.po.number') ?? readPath(evidence, 'po_number'),
  );
  const vendor = asString(
    readPath(evidence, 'content.po.vendor') ?? readPath(evidence, 'vendor'),
  );

  // Recompute total on every render — the editable-cells handler writes
  // qty/unit_price back through onFieldChange, which triggers a parent
  // re-render with the next `evidence`.
  const total = React.useMemo(
    () =>
      items.reduce(
        (acc, it) => acc + asFiniteNumber(it.qty) * asFiniteNumber(it.unit_price),
        0,
      ),
    [items],
  );

  // Cell edit handler — write the next line-items array atomically so
  // re-renders see a consistent grid (no partial updates between cells).
  const writeItems = React.useCallback(
    (next: LineItem[]) => {
      onFieldChange(lineItemsPath, next);
    },
    [lineItemsPath, onFieldChange],
  );

  const updateRow = React.useCallback(
    (rowIndex: number, patch: Partial<LineItem>) => {
      const next = items.map((row, i) =>
        i === rowIndex ? { ...row, ...patch } : row,
      );
      writeItems(next);
    },
    [items, writeItems],
  );

  const addRow = React.useCallback(() => {
    const next: LineItem[] = [
      ...items,
      { sku: '', description: '', qty: 1, unit_price: 0 },
    ];
    writeItems(next);
  }, [items, writeItems]);

  const removeRow = React.useCallback(
    (rowIndex: number) => {
      const next = items.filter((_, i) => i !== rowIndex);
      writeItems(next);
    },
    [items, writeItems],
  );

  // Editable header fields — anything the schema declares lives here. We
  // do NOT special-case canonical paths like the email renderer because PO
  // header fields are highly tenant-specific (some carry GL codes, cost
  // centers, project IDs, etc.) — we just surface whatever's declared.
  const headerFields = schema.editableFields;

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-4" data-renderer="purchase_order">
        {/* Header summary */}
        <header className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border/60 bg-muted/30 p-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-primary" />
            <div className="space-y-0.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Purchase order
              </div>
              <div className="text-sm font-semibold">
                {poNumber || '— no PO# —'}
                {vendor && (
                  <>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <span className="font-normal text-muted-foreground">
                      {vendor}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-0.5 text-right">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Total
            </div>
            <div
              className="text-lg font-semibold tabular-nums"
              data-po-total="true"
            >
              {fmtCurrency(total, currencyCode)}
            </div>
          </div>
        </header>

        {/* Header / editable fields declared by the workflow */}
        {headerFields.length > 0 && (
          <section className="space-y-3">
            {headerFields.map((f) => (
              <FieldRow
                key={f.path}
                approvalId={approval.id}
                field={f}
                value={readPath(evidence, f.path)}
                onChange={(v) => onFieldChange(f.path, v)}
                disabled={disabled}
              />
            ))}
          </section>
        )}

        {/* Line items grid */}
        <section data-line-items-grid="true">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Line items ({items.length})
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={disabled}
              data-add-line-item="true"
            >
              <Plus className="mr-1 h-3 w-3" />
              Add line
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs italic text-muted-foreground">
              No line items. Add one to populate this PO.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">SKU</th>
                    <th className="px-3 py-2 font-semibold">Description</th>
                    <th className="px-3 py-2 text-right font-semibold">Qty</th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Unit price
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Line total
                    </th>
                    <th className="px-3 py-2" aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => {
                    const qty = asFiniteNumber(row.qty);
                    const price = asFiniteNumber(row.unit_price);
                    const lineTotal = qty * price;
                    return (
                      <tr
                        key={i}
                        className="border-t border-border/60"
                        data-line-item-row={i}
                      >
                        <td className="px-2 py-1">
                          <Input
                            value={asString(row.sku)}
                            onChange={(e) =>
                              updateRow(i, { sku: e.target.value })
                            }
                            disabled={disabled}
                            className="h-8"
                            aria-label={`SKU row ${i + 1}`}
                            data-cell="sku"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={asString(row.description)}
                            onChange={(e) =>
                              updateRow(i, { description: e.target.value })
                            }
                            disabled={disabled}
                            className="h-8"
                            aria-label={`Description row ${i + 1}`}
                            data-cell="description"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="any"
                            min={0}
                            value={qty === 0 && row.qty === undefined ? '' : qty}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateRow(i, {
                                qty:
                                  v === ''
                                    ? 0
                                    : Number.isFinite(Number(v))
                                      ? Number(v)
                                      : 0,
                              });
                            }}
                            disabled={disabled}
                            className="h-8 text-right tabular-nums"
                            aria-label={`Qty row ${i + 1}`}
                            data-cell="qty"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={
                              price === 0 && row.unit_price === undefined
                                ? ''
                                : price
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              updateRow(i, {
                                unit_price:
                                  v === ''
                                    ? 0
                                    : Number.isFinite(Number(v))
                                      ? Number(v)
                                      : 0,
                              });
                            }}
                            disabled={disabled}
                            className="h-8 text-right tabular-nums"
                            aria-label={`Unit price row ${i + 1}`}
                            data-cell="unit_price"
                          />
                        </td>
                        <td
                          className="px-3 py-1 text-right tabular-nums"
                          data-cell="line_total"
                        >
                          {fmtCurrency(lineTotal, currencyCode)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={disabled}
                            onClick={() => removeRow(i)}
                            aria-label={`Remove line ${i + 1}`}
                            title="Remove line"
                            data-remove-line-item={i}
                            className="h-7 w-7"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-muted/30 text-sm">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right font-semibold">
                      Total
                    </td>
                    <td
                      className="px-3 py-2 text-right font-semibold tabular-nums"
                      data-po-grand-total="true"
                    >
                      {fmtCurrency(total, currencyCode)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        <ActionBar
          actions={schema.actions}
          approvalId={approval.id}
          onDecide={onDecide}
          disabled={disabled}
          className="rounded-md"
        />
      </div>

      {/* Right column — context panels (vendor card, prior POs, KB refs). */}
      <aside className="space-y-3">
        {schema.contextPanels.length === 0 && (
          <p className="text-xs italic text-muted-foreground">
            No context panels declared
          </p>
        )}
        {schema.contextPanels.map((panel) => (
          <section
            key={panel.panelId}
            className="rounded-md border border-border/60 bg-card p-3"
          >
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {panel.title}
            </h4>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px]">
              {JSON.stringify(
                panel.dataPath
                  ? readPath(evidence, panel.dataPath)
                  : panel.fields?.length
                    ? Object.fromEntries(
                        panel.fields.map((f) => [
                          f.label,
                          readPath(evidence, f.path) ?? null,
                        ]),
                      )
                    : evidence,
                null,
                2,
              )}
            </pre>
          </section>
        ))}
      </aside>
    </div>
  );
};
